import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type AttendanceVerificationMethodConfig,
  type ShiftStateMetadata,
} from "@src/appV2/Shifts/Shift/ShiftState/types";

/**
 * List of attendance verification methods that are supported by the current mobile app version.
 * When new methods are added to the backend, they must be added here once the UI is implemented.
 * This prevents old app versions from trying to use unsupported methods.
 */
const SUPPORTED_ATTENDANCE_VERIFICATION_METHODS = new Set<AttendanceVerificationMethod>([
  AttendanceVerificationMethod.GEOFENCE,
  AttendanceVerificationMethod.NFC_TAG,
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  AttendanceVerificationMethod.MESH_INTEGRATION,
  AttendanceVerificationMethod.TAKE_A_VIDEO,
  AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
]);

interface UseNewAttendanceVerificationMethodParams {
  shiftStateMetadata?: ShiftStateMetadata;
}

/**
 * Returns all enabled attendance verification methods from the new configuration.
 * Methods are returned as-is from the backend (already in priority order).
 * Unsupported methods are filtered out to prevent old app versions from breaking
 * when new methods are added to the backend.
 */
export function useNewAttendanceVerificationMethods(
  params: UseNewAttendanceVerificationMethodParams
): AttendanceVerificationMethodConfig[] {
  const { shiftStateMetadata } = params;

  const attendanceVerificationMethods = shiftStateMetadata?.attendanceVerificationMethods?.filter(
    (method) => method.isEnabled && SUPPORTED_ATTENDANCE_VERIFICATION_METHODS.has(method.type)
  );

  if (!isDefined(attendanceVerificationMethods) || attendanceVerificationMethods.length === 0) {
    return [
      {
        type: AttendanceVerificationMethod.GEOFENCE,
        isEnabled: true,
      },
    ];
  }

  return attendanceVerificationMethods;
}
